// Поля тарифа. Общие для мастера постановки на объект и для изменения тарифа: форма одна,
// потому что и правило одно.
//
// ⛔ ГЛАВНОЕ. Ставка не вводится числом — кроме режима «прямая ставка», ради которого он и
// заведён. Причина не в удобстве, а в числе 5 044 (server/app/domain/payroll/rate.py):
// целевая 74 516 при 348 часах даёт ставку 214,126437, показывается 214, а обратный ход через
// введённые 214 даёт 74 472 — взыскание молча выросло на 5 044 рубля. Направление расчёта —
// часть денежного смысла, а не деталь ввода.
//
// Поэтому здесь вводят ПАРУ, из которой ставка получается: сумму за период и норму часов либо
// целевую сумму и отработанные часы. Саму ставку считает сервер и возвращает готовой; на
// клиенте её не считаем даже для показа (правило 5: деньги считаются только на сервере),
// иначе округление разъедется с движком, и человек увидит на экране одно, а в ведомости другое.

import type { RateMode } from '@/shared/api/endpoints/people';
import { decimalString } from '@/shared/api/types';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { RATE_MODE_TITLES } from '../dictionaries';

export interface RateFormState {
  mode: RateMode;
  /** Рубли, как их пишет человек: «85 000», «85000,50». */
  periodAmount: string;
  /** Часы десятичной строкой: «558», «63.5». */
  normHours: string;
  /** Рубли. */
  targetAmount: string;
  /** Часы. */
  factHours: string;
  /** Рубли в час — ЕДИНСТВЕННОЕ поле ставки во всей системе. */
  hourlyRate: string;
}

export const EMPTY_RATE_FORM: RateFormState = {
  mode: 'by_period',
  periodAmount: '',
  normHours: '',
  targetAmount: '',
  factHours: '',
  hourlyRate: '',
};

export type RateFieldName =
  'period_amount' | 'norm_hours' | 'target_amount' | 'fact_hours_for_target' | 'hourly_rate';

export interface RateValues {
  rate_mode: RateMode;
  period_amount?: number | null;
  norm_hours?: string | null;
  target_amount?: number | null;
  fact_hours_for_target?: string | null;
  hourly_rate?: string | null;
}

export type RateBuildResult =
  { ok: true; values: RateValues } | { ok: false; errors: Partial<Record<RateFieldName, string>> };

/** Проверка и сборка полей тарифа. Отдельно от разметки, чтобы её гоняли тесты. */
export function buildRateValues(state: RateFormState): RateBuildResult {
  const errors: Partial<Record<RateFieldName, string>> = {};

  if (state.mode === 'by_period') {
    const amount = parseRublesToKopecks(state.periodAmount);
    if (!amount.ok) {
      errors.period_amount = moneyErrorText(amount.reason, 'сумму за период');
    }
    const hours = parseHours(state.normHours);
    if (hours === null) {
      errors.norm_hours = 'Норма часов пишется числом: 558 или 63.5. Её берут из объекта.';
    }
    if (!amount.ok || hours === null) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      values: { rate_mode: 'by_period', period_amount: amount.value, norm_hours: hours },
    };
  }

  if (state.mode === 'by_target') {
    const amount = parseRublesToKopecks(state.targetAmount);
    if (!amount.ok) {
      errors.target_amount = moneyErrorText(amount.reason, 'целевую сумму');
    }
    const hours = parseHours(state.factHours);
    if (hours === null) {
      errors.fact_hours_for_target =
        'Отработанные часы пишутся числом: 348 или 347.5. Из них и целевой суммы получится ставка.';
    }
    if (!amount.ok || hours === null) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      values: {
        rate_mode: 'by_target',
        target_amount: amount.value,
        fact_hours_for_target: hours,
      },
    };
  }

  const rate = parseHours(state.hourlyRate);
  if (rate === null) {
    return {
      ok: false,
      errors: { hourly_rate: 'Ставка пишется числом рублей в час: 165 или 152.33.' },
    };
  }
  return { ok: true, values: { rate_mode: 'direct', hourly_rate: rate } };
}

/** Дробная величина строкой. Запятая из привычки Excel принимается, точка уезжает на сервер. */
function parseHours(input: string): string | null {
  const cleaned = input.trim().replace(',', '.');
  if (cleaned === '') {
    return null;
  }
  try {
    return decimalString(cleaned);
  } catch {
    return null;
  }
}

/**
 * Что означает каждый способ, одной фразой.
 *
 * Стоит подсказкой у выбора, а не в голове у финансового отдела: «от целевой суммы» — это не
 * очевидное название, а именно им делают взыскание, и ошибка здесь стоит 5 044 рублей.
 */
const ПОЯСНЕНИЕ_РЕЖИМА: Record<RateMode, string> = {
  by_period: 'Человек получает сумму за полный период; ставку считает сервер от неё и нормы.',
  by_target: 'Задаётся сумма к начислению за отработанные часы; так же делается взыскание.',
  direct: 'Ставка задаётся числом. Только для объектов с фиксированной ценой часа.',
};

export interface RateFieldsProps {
  idPrefix: string;
  value: RateFormState;
  onChange: (next: RateFormState) => void;
  errors: Partial<Record<RateFieldName, string>>;
}

export function RateFields({ idPrefix, value, onChange, errors }: RateFieldsProps) {
  function patch(part: Partial<RateFormState>) {
    onChange({ ...value, ...part });
  }

  return (
    <div className='flex flex-col gap-3'>
      {/* Справочник, а не переключатель из трёх кнопок в ряд. Замер 25.08.2026 на живой
          панели шириной 350 точек: три названия по 16-19 знаков в равных долях не помещались,
          у кнопок стоит `whitespace-nowrap` — и подписи налезали друг на друга, «От суммы за
          период» печаталось поверх «От целевой суммыПрямая ставка». Переключатель из трёх
          кнопок вообще не для длинных названий: он хорош на «Все | Долги» и ломается на любой
          фразе. Справочник же выдерживает подпись любой длины и стоит в том же ряду, что и
          все остальные выборы этой панели. */}
      <SelectField
        id={`${idPrefix}-rate-mode`}
        label='Как считается тариф'
        value={value.mode}
        onChange={(next) => patch({ mode: next as RateMode })}
        options={[
          { value: 'by_period', label: RATE_MODE_TITLES.by_period },
          { value: 'by_target', label: RATE_MODE_TITLES.by_target },
          { value: 'direct', label: RATE_MODE_TITLES.direct },
        ]}
        placeholder='Выберите способ'
        hint={ПОЯСНЕНИЕ_РЕЖИМА[value.mode]}
      />

      {value.mode === 'by_period' && (
        <>
          <TextField
            id={`${idPrefix}-period-amount`}
            label='Сумма за период, рублей'
            required
            inputMode='decimal'
            value={value.periodAmount}
            onChange={(next) => patch({ periodAmount: next })}
            placeholder='85 000'
            error={errors.period_amount}
            hint='Столько человек получает за полный период при полной норме.'
          />
          <TextField
            id={`${idPrefix}-norm-hours`}
            label='Норма часов за период'
            required
            inputMode='decimal'
            value={value.normHours}
            onChange={(next) => patch({ normHours: next })}
            placeholder='558'
            error={errors.norm_hours}
            hint='Берётся из объекта: сумма часов в сутки по постам, умноженная на дни периода.'
          />
        </>
      )}

      {value.mode === 'by_target' && (
        <>
          <TextField
            id={`${idPrefix}-target-amount`}
            label='Целевая сумма, рублей'
            required
            inputMode='decimal'
            value={value.targetAmount}
            onChange={(next) => patch({ targetAmount: next })}
            placeholder='74 516'
            error={errors.target_amount}
            hint='Столько человек должен получить на руки по итогу — например после взыскания.'
          />
          <TextField
            id={`${idPrefix}-fact-hours`}
            label='Отработанные часы'
            required
            inputMode='decimal'
            value={value.factHours}
            onChange={(next) => patch({ factHours: next })}
            placeholder='348'
            error={errors.fact_hours_for_target}
            hint='Из целевой суммы и этих часов получится ставка — обратный ход через ставку теряет рубли.'
          />
        </>
      )}

      {value.mode === 'direct' && (
        <TextField
          id={`${idPrefix}-hourly-rate`}
          label='Ставка, рублей в час'
          required
          inputMode='decimal'
          value={value.hourlyRate}
          onChange={(next) => patch({ hourlyRate: next })}
          placeholder='165'
          error={errors.hourly_rate}
          hint='Единственный режим, где ставку задают напрямую: объекты с фиксированной ставкой.'
        />
      )}

      {/* Почему считает именно сервер — это наша инженерная кухня, и человеку она не
          показывается. Ему важно одно: ставка появится в карточке назначения, вводить её
          отдельно не надо. Причина записана в шапке файла, где ей и место. */}
      <p className='text-paragraph-xs text-text-sub-600'>
        {value.mode === 'direct'
          ? 'Ставка уйдёт как есть и появится в карточке назначения.'
          : 'Ставку посчитает система и покажет в карточке назначения: вводить её не надо.'}
      </p>
    </div>
  );
}
